import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, MessageSquareQuote, Quote } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { api } from '../lib/api';
import { FACEBOOK_REVIEWS_URL } from '../lib/social';

// Loads ONLY approved reviews from the public endpoint.
export const useApprovedReviews = (limit = 50) => {
  const [state, setState] = useState({ reviews: [], total: 0, loading: true, error: false });
  useEffect(() => {
    let alive = true;
    api.get('/reviews', { params: { limit } })
      .then((r) => { if (alive) setState({ reviews: r.data?.reviews || [], total: r.data?.total || 0, loading: false, error: false }); })
      .catch(() => { if (alive) setState({ reviews: [], total: 0, loading: false, error: true }); });
    return () => { alive = false; };
  }, [limit]);
  return state;
};

export const ReviewCard = ({ review, compact = false, testId }) => (
  <Card className={`h-full ${compact ? '' : 'card-lift'}`} data-testid={testId || `review-card-${review.id}`}>
    <CardContent className={`${compact ? 'p-5' : 'p-6'} flex flex-col h-full`}>
      <Quote className="h-6 w-6 text-[#C9A84C]" aria-hidden="true" />
      <p className={`mt-3 text-slate-700 leading-relaxed flex-1 ${compact ? 'text-sm' : 'text-[15px]'}`} data-testid="review-text">{review.review_text}</p>
      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="font-serif font-bold text-[#1a2744]" data-testid="review-name">{review.display_name}</p>
        {review.context && <p className="text-xs text-slate-500 mt-0.5" data-testid="review-context">{review.context}</p>}
        {review.reply_text && (
          <div className="mt-3 rounded-lg bg-[#f3f7ff] border border-[#c9d8f0] p-3" data-testid="review-reply">
            <p className="text-[10px] font-black uppercase tracking-wider text-[#8a6d2c]">Reply from Steve</p>
            <p className="text-sm text-[#1a2744] mt-1 leading-relaxed" data-testid="review-reply-text">{review.reply_text}</p>
          </div>
        )}
        {review.source_url && (
          <a href={review.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-2 mt-2" data-testid="review-source-link">
            View original review{review.source_label ? ` on ${review.source_label}` : ''} <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </CardContent>
  </Card>
);

export const FacebookReviewsButton = ({ variant = 'outline', className = '', testId = 'facebook-reviews-button' }) => (
  <Button asChild variant={variant} className={`h-12 px-6 font-bold btn-press justify-center ${className}`} data-testid={testId}>
    <a href={FACEBOOK_REVIEWS_URL} target="_blank" rel="noopener noreferrer">Read &amp; Leave Facebook Reviews <ExternalLink className="h-4 w-4 ml-1.5" /></a>
  </Button>
);

// Homepage section: featured first (admin order), then newest approved.
export const ReviewsHomeSection = () => {
  const { reviews, total, loading } = useApprovedReviews(6);
  const shown = reviews.slice(0, 3);
  return (
    <section className="bg-[#f3f7ff] border-b border-slate-200 py-12 md:py-16" data-testid="home-reviews-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div className="max-w-2xl">
            <p className="text-xs font-black uppercase tracking-widest text-[#8a6d2c]">REVIEWS AND TESTIMONIALS</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-black text-[#1a2744] tracking-tight leading-tight" data-testid="home-reviews-headline">WHAT PEOPLE SAY ABOUT WORKING WITH STEVE</h2>
            <p className="mt-3 text-slate-600 leading-relaxed" data-testid="home-reviews-intro">Real experiences from residents, colleagues and clients. Reviews are checked before they appear here.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild className="h-12 px-6 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press justify-center" data-testid="home-leave-review-button">
              <Link to="/leave-a-review"><MessageSquareQuote className="h-4 w-4 mr-2" /> Leave a Website Review</Link>
            </Button>
            <FacebookReviewsButton className="border-2 border-[#1a2744] text-[#1a2744]" testId="home-facebook-reviews-button" />
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="home-reviews-grid">
          {loading && [0, 1, 2].map((i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
          {!loading && shown.map((r) => <ReviewCard key={r.id} review={r} compact />)}
          {!loading && shown.length === 0 && (
            <Card className="md:col-span-3" data-testid="home-reviews-empty">
              <CardContent className="p-6 text-center">
                <p className="font-serif font-bold text-[#1a2744]">Be the first to share your experience with Steve.</p>
                <p className="text-sm text-slate-600 mt-1">Approved website reviews will appear here. You can also read and leave reviews on Facebook.</p>
              </CardContent>
            </Card>
          )}
        </div>
        <div className="mt-6">
          <Link to="/reviews" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-read-more-reviews-link">
            Read more reviews{total > 0 ? ` (${total})` : ''} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};
